import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, Ban } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUS_STYLE = {
    active: "text-avx-success border-avx-success/40",
    redeemed: "text-avx-muted border-avx-line",
    void: "text-avx-danger border-avx-danger/40",
};

export default function AdminSerials() {
    const [products, setProducts] = useState([]);
    const [serials, setSerials] = useState([]);
    const [genProduct, setGenProduct] = useState("");
    const [quantity, setQuantity] = useState(5);
    const [note, setNote] = useState("");
    const [generating, setGenerating] = useState(false);
    const [filterProduct, setFilterProduct] = useState("all");
    const [filterStatus, setFilterStatus] = useState("all");
    const [search, setSearch] = useState("");

    useEffect(() => { api.get("/admin/products").then(({ data }) => setProducts(data || [])); }, []);

    const load = () => {
        const params = {};
        if (filterProduct !== "all") params.product_id = filterProduct;
        if (filterStatus !== "all") params.status = filterStatus;
        if (search.trim()) params.q = search.trim();
        api.get("/admin/serials", { params }).then(({ data }) => setSerials(data || []));
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterProduct, filterStatus, search]);

    const generate = async (e) => {
        e.preventDefault();
        if (!genProduct) { toast.error("Select a product"); return; }
        setGenerating(true);
        try {
            const { data } = await api.post("/admin/serials", {
                product_id: genProduct,
                quantity: Math.max(1, Math.min(500, Number(quantity) || 1)),
                note,
            });
            toast.success(`${data.codes.length} serial(s) generated for ${data.product_name}`);
            setNote("");
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setGenerating(false);
        }
    };

    const copy = (code) => { navigator.clipboard.writeText(code); toast.success("Copied"); };

    const voidSerial = async (id) => {
        try { await api.post(`/admin/serials/${id}/void`); toast.success("Serial voided"); load(); }
        catch (err) { toast.error(apiErrorMessage(err)); }
    };
    const del = async (id) => {
        try { await api.delete(`/admin/serials/${id}`); toast.success("Serial deleted"); load(); }
        catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const copyAllActive = () => {
        const codes = serials.filter((s) => s.status === "active").map((s) => s.code);
        if (!codes.length) { toast.error("No active serials to copy"); return; }
        navigator.clipboard.writeText(codes.join("\n"));
        toast.success(`Copied ${codes.length} active code(s)`);
    };

    return (
        <DashboardLayout role="admin">
            <div className="mb-8 flex items-end justify-between flex-wrap gap-4" data-testid="admin-serials-page">
                <div>
                    <div className="label-mono mb-3">/ Serials</div>
                    <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Serial codes</h1>
                    <p className="text-avx-muted mt-2 max-w-2xl">
                        Generate single-use codes for a product. Clients redeem them from their dashboard to register the plugin for free.
                    </p>
                </div>
                <button onClick={copyAllActive} className="btn-secondary px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] rounded-sm" data-testid="serials-copy-active">
                    Copy active codes
                </button>
            </div>

            {/* Generator */}
            <form onSubmit={generate} className="panel p-6 mb-8 grid md:grid-cols-4 gap-4 items-end" data-testid="serials-generate-form">
                <div className="md:col-span-2">
                    <Label className="label-mono">Product</Label>
                    <Select value={genProduct} onValueChange={setGenProduct}>
                        <SelectTrigger className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="serials-product-select">
                            <SelectValue placeholder="Select product" />
                        </SelectTrigger>
                        <SelectContent>
                            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="label-mono">Quantity</Label>
                    <Input type="number" min="1" max="500" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="serials-quantity" />
                </div>
                <button type="submit" disabled={generating} className="btn-primary h-11 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-60" data-testid="serials-generate-btn">
                    {generating ? "Generating…" : "Generate"}
                </button>
                <div className="md:col-span-4">
                    <Label className="label-mono">Note (optional)</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Resellers batch #3" className="mt-2 bg-avx-bg border-avx-line h-11" data-testid="serials-note" />
                </div>
            </form>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-4">
                <Input placeholder="Search code…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-avx-bg border-avx-line h-10 w-56" data-testid="serials-search" />
                <Select value={filterProduct} onValueChange={setFilterProduct}>
                    <SelectTrigger className="bg-avx-bg border-avx-line h-10 w-56" data-testid="serials-filter-product"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All products</SelectItem>
                        {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="bg-avx-bg border-avx-line h-10 w-44" data-testid="serials-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="redeemed">Redeemed</SelectItem>
                        <SelectItem value="void">Void</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Table */}
            <div className="panel overflow-hidden" data-testid="serials-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-avx-line label-mono text-left">
                                <th className="p-3">Code</th>
                                <th className="p-3">Product</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Redeemed by</th>
                                <th className="p-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {serials.length === 0 ? (
                                <tr><td colSpan={5} className="p-6 text-center text-avx-muted">No serials yet.</td></tr>
                            ) : serials.map((s) => (
                                <tr key={s.id} className="border-b border-avx-line/50" data-testid={`serial-row-${s.id}`}>
                                    <td className="p-3 font-mono tracking-wider">{s.code}</td>
                                    <td className="p-3">{s.product_name || "—"}</td>
                                    <td className="p-3">
                                        <span className={`text-[10px] font-mono uppercase tracking-[0.15em] border rounded-sm px-2 py-0.5 ${STATUS_STYLE[s.status] || ""}`}>
                                            {s.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-avx-muted">{s.redeemed_by_user?.email || "—"}</td>
                                    <td className="p-3">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => copy(s.code)} title="Copy" className="p-2 text-avx-muted hover:text-avx-blue" data-testid={`serial-copy-${s.id}`}><Copy className="w-4 h-4" /></button>
                                            {s.status === "active" && (
                                                <button onClick={() => voidSerial(s.id)} title="Void" className="p-2 text-avx-muted hover:text-avx-danger" data-testid={`serial-void-${s.id}`}><Ban className="w-4 h-4" /></button>
                                            )}
                                            {s.status !== "redeemed" && (
                                                <button onClick={() => del(s.id)} title="Delete" className="p-2 text-avx-muted hover:text-avx-danger" data-testid={`serial-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </DashboardLayout>
    );
}
